import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatBubbleOvalLeftIcon, HandThumbUpIcon } from '@heroicons/react/24/outline';
import { formatNumberCompact } from '@shared/dream-components';
import cn from 'classnames';

import type { CommunityPost } from '~/interfaces/community';
import { communityPostPath } from '~/routes/community/_utils/communityPostPath';
import { isResourceDiscussionHidden } from '~/routes/community/_utils/isResourceDiscussionHidden';

import { PermissionGate } from '../../PermissionGate';
import { actionIconClass, countClass, hoverPillClass } from '../constants';
import { DiscussionOffButton } from './DiscussionOffButton';
import { ReactionPicker } from './ReactionPicker';
import { RepostMenu } from './RepostMenu';
import { ShareMenu } from './ShareMenu';

interface PostCardActionsProps {
  post: CommunityPost;
  commentsCount?: number;
  isCommentsExpanded?: boolean;
  onToggleComments?: () => void;
}

export const PostCardActions: FC<PostCardActionsProps> = ({
  post,
  commentsCount = post.comments_count,
  isCommentsExpanded = false,
  onToggleComments,
}) => {
  const { t } = useTranslation();
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}${communityPostPath(post)}` : '';
  const isCommentsHidden = post.comments_state === 'hidden';
  const isDiscussionHidden = isResourceDiscussionHidden(post);

  return (
    <div className="flex items-center justify-between px-2 pb-4">
      {isDiscussionHidden ? (
        <DiscussionOffButton label={t('community.reactions.react')} icon={HandThumbUpIcon} />
      ) : (
        <ReactionPicker
          key={post.id}
          reactableType="CommunityPost"
          reactableId={post.id}
          reactionsCount={post.reactions_count}
          memberReactions={post.current_member_reactions ?? []}
        />
      )}

      {isDiscussionHidden ? (
        <DiscussionOffButton label={t('community.content_page.comments')} icon={ChatBubbleOvalLeftIcon} />
      ) : (
        <button
          type="button"
          onClick={onToggleComments}
          aria-label={isCommentsExpanded ? t('community.comments.aria_hide') : t('community.comments.aria_show')}
          aria-expanded={isCommentsExpanded}
          className={cn(hoverPillClass, 'gap-1', {
            'bg-black/5': isCommentsExpanded,
          })}
        >
          <ChatBubbleOvalLeftIcon className={actionIconClass} />
          {!isCommentsHidden && !!commentsCount && (
            <span className={countClass}>{formatNumberCompact(commentsCount)}</span>
          )}
        </button>
      )}

      <PermissionGate require="canCreatePost">
        <RepostMenu post={post} />
      </PermissionGate>

      <ShareMenu url={shareUrl} title={post.title} />
    </div>
  );
};
